import { useTranslation } from 'react-i18next'
import CalendarIcon from '@mui/icons-material/CalendarMonthOutlined'
import PlaceIcon from '@mui/icons-material/PlaceOutlined'
import HomeIcon from '@mui/icons-material/HomeOutlined'
import FridgeIcon from '@mui/icons-material/KitchenOutlined'
import ArrowIcon from '@mui/icons-material/KeyboardArrowRight'
import ManIcon from '@mui/icons-material/Person'
import TruckIcon from '@mui/icons-material/LocalShipping'
import TimerIcon from '@mui/icons-material/Timer'
import EditIcon from '@mui/icons-material/Edit'

import { useCurrentMove } from '../../modules/moves'
import { useNavigate } from '../../modules/navigation'
import { Room } from '../../common/residence'
import { SpecialItem } from '../../common/specialItem'
import { useDateFormatter } from '../../utils/date'
import { mergeClassName } from '../../utils/mergeClassName'
import { capitalizeFirstLetter } from '../../utils/string'
import Value from './Value'
import { type Place } from '../../common/place'
import MultiValues from './MultiValues'
import ItemsRow from './ItemsRow'
import Button, { ButtonColor, ButtonSize, ButtonVariant } from '../Button'
import Route from '../../app/Route.enum'
import { getMoveReferenceLocations } from '../../modules/moves/core/locations.utils'
import { getMoveDate, getMoveNbMen, getMoveNbTruck } from '../../modules/moves/core/resolved.utils'

const MoveSummaryBanner: React.FC = () => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const formatDate = useDateFormatter()
  const { navigate } = useNavigate()

  const { origin, destination } = getMoveReferenceLocations(move)
  const nbMen = getMoveNbMen(move)
  const nbTrucks = getMoveNbTruck(move)
  const moveDate = getMoveDate(move)

  const getAddress = (place: Place) => {
    if ('placeTextLine1' in place && place.placeTextLine1) {
      return place.placeTextLine1
    }
    if ('city' in place && 'region' in place && place.city) {
      return `${capitalizeFirstLetter(place.city) ?? ''}, ${place.region ?? ''}`
    }
    if ('postalCode' in place && place.postalCode) {
      return place.postalCode.replace(' ', '').toUpperCase()
    }
    return null
  }

  /**
   * returns a string that summarize the room of the residence
   */
  const getRoomsSummary = () => {
    if (!move.request.residence.residenceRooms?.length) {
      return null
    }
    const totalRooms = move.request.residence.residenceRooms.reduce(
      (acc, curr) => Number(acc) + Number(curr.value),
      0,
    )
    if (totalRooms === 0) {
      return null
    }
    const nbBedrooms = move.request.residence.residenceRooms.find(({ key }) => key === Room.bedroom)?.value ?? 0
    const otherRooms = Math.max(totalRooms - nbBedrooms, 0)

    if (nbBedrooms > 0 && otherRooms > 0) {
      return (
        <div className="flex flex-wrap gap-x-1">
          <div>
            { t('pages.move.quotes.rooms.bedroom', { count: nbBedrooms }) }
          </div>
          <div>
            { t('pages.move.quotes.rooms.plus', { count: otherRooms }) }
          </div>
        </div>
      )
    }
    if (nbBedrooms > 0) {
      return t('pages.move.quotes.rooms.bedroom', { count: nbBedrooms })
    }
    return t('pages.move.quotes.rooms.total', { count: otherRooms })
  }

  /**
   * returns a string that summarize the special items to move
   */
  const getSpecialItemsSummary = () => {
    if (!move.request.content.specialItems?.length) {
      return null
    }
    const totalItems = move.request.content.specialItems.reduce(
      (acc, curr) => Number(acc) + Number(curr.value),
      0,
    )
    if (totalItems === 0) {
      return null
    }
    const nbAppliances = move.request.content.specialItems.find(({ key }) => key === SpecialItem.appliance)?.value ?? 0
    const otherItems = Math.max(totalItems - nbAppliances, 0)

    if (nbAppliances > 0 && otherItems > 0) {
      return (
        <div className="flex flex-wrap gap-x-1">
          <div>
            { t('pages.move.quotes.specialItems.appliance', { count: nbAppliances }) }
          </div>
          <div>
            { t('pages.move.quotes.specialItems.plus', { count: otherItems }) }
          </div>
        </div>
      )
    }
    if (nbAppliances > 0) {
      return t('pages.move.quotes.specialItems.appliance', { count: nbAppliances })
    }
    return t('pages.move.quotes.specialItems.total', { count: otherItems })
  }

  const getTrucksSummary = () => {
    if (!nbTrucks) {
      return
    }
    return (
      <div>
        { nbTrucks.map(({ key, value }) => (
          <div key={key}>
            { t('nbTrucks', { count: value }) }
            { ' ' }
            ({ t(`trucks.${key}.length`) })
          </div>
        )) }
      </div>
    )
  }

  const roomsSummary = getRoomsSummary()
  const specialItemsSummary = getSpecialItemsSummary()
  const trucksSummary = getTrucksSummary()

  const nbStops = move.locations.stops?.length ?? 0

  return (
    <div>
      <div className={mergeClassName(
        'sm:bg-gradient-to-t from-white to-zinc-400/20',
        'border-b border-b-zinc-200 px-2 py-4 sm:py-8 text-center sm:px-4 md:px-6 lg:px-12',
      )}
      >

        <div className="flex flex-col flex-wrap items-center gap-4 xl:flex-row xl:justify-around xl:gap-0">
          <div className="flex flex-row flex-wrap justify-center gap-2 xl:flex-col">
            <ItemsRow>
              { moveDate && (
                <Value
                  Icon={CalendarIcon}
                  label={t('pages.move.quotes.date')}
                >
                  <div className="whitespace-nowrap">
                    { formatDate(moveDate, { shortMonth: true }) }
                    { move.jobs.length > 1 && (
                      <>
                        { ' ' }
                        ({ t('pages.move.quotes.nbDays', { count: move.jobs.length }) })
                      </>
                    ) }
                  </div>
                </Value>
              ) }

              <MultiValues
                Icon={PlaceIcon}
                separator={<ArrowIcon />}
              >
                { origin && (
                  <Value
                    label={t('pages.move.quotes.from')}
                  >
                    { getAddress(origin.address) }
                  </Value>
                ) }

                { destination && origin !== destination && (
                  <Value
                    label={t('pages.move.quotes.to')}
                  >
                    { getAddress(destination.address) }
                  </Value>
                ) }
              </MultiValues>
            </ItemsRow>

            { (nbStops > 0 || !!roomsSummary || !!specialItemsSummary) && (
              <ItemsRow>

                { nbStops > 0 && (
                  <Value
                    Icon={PlaceIcon}
                    label={t('pages.move.quotes.nbStops', { count: nbStops })}
                    className="sm:max-w-none"
                  >
                    <div className="w-full text-center">
                      { nbStops }
                    </div>
                  </Value>
                ) }

                { roomsSummary && (
                  <Value
                    Icon={HomeIcon}
                    label={t('rooms.title')}
                    className="hidden max-w-[160px] sm:block sm:max-w-none"
                  >
                    { roomsSummary }
                  </Value>
                ) }

                { specialItemsSummary && (
                  <Value
                    Icon={FridgeIcon}
                    label={t('specialItems.title')}
                  >
                    { specialItemsSummary }
                  </Value>
                ) }

              </ItemsRow>
            ) }

          </div>

          <div className="hidden h-[75px] w-px bg-neutral-300 xl:flex" />

          <div className="hidden flex-col items-center justify-center gap-x-4 sm:flex">
            <div className="mb-2 font-sans text-sm text-neutral-500 sm:mt-0">
              { t('pages.move.quotes.estimatedMoveSize') }
              { ': ' }
              <strong className="text-base">
                { move.request.size.volumeCubicFeet }
                { ' ' }
                { t('units.cubicFeet') }
              </strong>
            </div>

            <ItemsRow>
              { nbMen && (
                <Value
                  label={t('men')}
                  Icon={ManIcon}
                  inverted
                >
                  { t('nbMen', { count: nbMen }) }
                </Value>
              ) }

              { trucksSummary && (
                <Value
                  label={t('trucks.title')}
                  Icon={TruckIcon}
                  inverted
                >
                  { trucksSummary }
                </Value>
              ) }

              { move.request.requirements?.movingLabourTime && (
                <Value
                  Icon={TimerIcon}
                  label={t('pages.move.quotes.labour')}
                  inverted
                >
                  { move.request.requirements.movingLabourTime }h
                </Value>
              ) }
            </ItemsRow>
          </div>

          <div className="hidden h-[75px] w-px bg-neutral-300 xl:flex" />

          <div className="sm:my-2 xl:mt-0">
            <Button
              startIcon={<EditIcon />}
              variant={ButtonVariant.Outlined}
              color={ButtonColor.Secondary}
              size={ButtonSize.Small}
              onClick={() => { navigate(Route.EditMove) }}
            >
              { t('actions.edit') }
            </Button>
          </div>

        </div>

      </div>
    </div>
  )
}

export default MoveSummaryBanner
